import { useCallback, useEffect, useRef, useState } from 'react';
import { X, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, ExternalLink, Move } from 'lucide-react';
import { toAbsoluteFotoUrl } from '@/utils/foto-url';

export interface FotoAmpliavel {
  url: string;
  /** Versão ampliada gerada na importação; usada a partir do 2º nível de zoom. */
  urlHd?: string | null;
  legenda?: string | null;
}

interface Props {
  fotos: FotoAmpliavel[];
  indiceInicial: number;
  onFechar: () => void;
}

/**
 * Passos de ampliação, em múltiplos do tamanho que cabe no card. Vão longe
 * porque a origem é pequena: a foto embutida na AR tem cerca de 345×260 px, e
 * para enxergar a fixação de uma proteção é preciso passar bem do tamanho
 * nativo.
 */
const ZOOMS = [1, 1.5, 2, 3, 4, 6];

/**
 * Card grande de foto, com zoom arrastável.
 *
 * O zoom altera a LARGURA da imagem, não um `transform: scale` — com transform
 * a imagem crescia visualmente mas continuava ocupando o mesmo espaço no
 * layout, então o contêiner não ganhava rolagem e o que passava da borda
 * simplesmente sumia.
 *
 * Ampliar mantém o ponto que estava no centro da tela, e arrastar navega pela
 * imagem: sem isso, a cada passo de zoom a pessoa perdia de vista o detalhe que
 * queria olhar.
 */
export function VisualizadorFoto({ fotos, indiceInicial, onFechar }: Props) {
  const [indice, setIndice] = useState(indiceInicial);
  const [nivelZoom, setNivelZoom] = useState(0);
  const [dimensao, setDimensao] = useState<{ w: number; h: number } | null>(null);
  const [arrastando, setArrastando] = useState(false);

  const areaRef = useRef<HTMLDivElement>(null);
  const arrasteRef = useRef({ x: 0, y: 0, esquerda: 0, topo: 0 });
  /** Ponto focal (0–1) a preservar no próximo render depois de mudar o zoom. */
  const focoRef = useRef<{ x: number; y: number } | null>(null);

  const zoom = ZOOMS[nivelZoom];

  const trocar = useCallback((passo: number) => {
    setIndice((i) => (i + passo + fotos.length) % fotos.length);
    setNivelZoom(0);
    setDimensao(null);
  }, [fotos.length]);

  /** Guarda o que está no centro da área antes de o tamanho da imagem mudar. */
  const guardarFoco = useCallback(() => {
    const area = areaRef.current;
    if (!area) return;
    const larguraRolavel = area.scrollWidth - area.clientWidth;
    const alturaRolavel = area.scrollHeight - area.clientHeight;
    focoRef.current = {
      x: larguraRolavel > 0 ? (area.scrollLeft + area.clientWidth / 2) / area.scrollWidth : 0.5,
      y: alturaRolavel > 0 ? (area.scrollTop + area.clientHeight / 2) / area.scrollHeight : 0.5,
    };
  }, []);

  const mudarZoom = useCallback((delta: number) => {
    guardarFoco();
    setNivelZoom((z) => Math.max(0, Math.min(ZOOMS.length - 1, z + delta)));
  }, [guardarFoco]);

  const redefinirZoom = useCallback(() => {
    focoRef.current = null;
    setNivelZoom(0);
  }, []);

  // Recentraliza no ponto focal depois que o novo tamanho já está no layout.
  useEffect(() => {
    const area = areaRef.current;
    const foco = focoRef.current;
    if (!area || !foco) return;
    area.scrollLeft = foco.x * area.scrollWidth - area.clientWidth / 2;
    area.scrollTop = foco.y * area.scrollHeight - area.clientHeight / 2;
    focoRef.current = null;
  }, [nivelZoom]);

  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFechar();
      else if (e.key === 'ArrowLeft' && fotos.length > 1) trocar(-1);
      else if (e.key === 'ArrowRight' && fotos.length > 1) trocar(1);
      else if (e.key === '+' || e.key === '=') mudarZoom(1);
      else if (e.key === '-') mudarZoom(-1);
      else if (e.key === '0') redefinirZoom();
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [trocar, onFechar, fotos.length, mudarZoom, redefinirZoom]);

  // --- Arraste ---
  // Feito com pointer events e captura no elemento, para o arraste continuar
  // funcionando quando o cursor sai da área — e para valer no touch também.
  const iniciarArraste = (e: React.PointerEvent) => {
    const area = areaRef.current;
    if (!area || zoom === 1) return;
    e.preventDefault();
    area.setPointerCapture(e.pointerId);
    arrasteRef.current = { x: e.clientX, y: e.clientY, esquerda: area.scrollLeft, topo: area.scrollTop };
    setArrastando(true);
  };

  const moverArraste = (e: React.PointerEvent) => {
    const area = areaRef.current;
    if (!area || !arrastando) return;
    area.scrollLeft = arrasteRef.current.esquerda - (e.clientX - arrasteRef.current.x);
    area.scrollTop = arrasteRef.current.topo - (e.clientY - arrasteRef.current.y);
  };

  const encerrarArraste = (e: React.PointerEvent) => {
    if (!arrastando) return;
    areaRef.current?.releasePointerCapture(e.pointerId);
    setArrastando(false);
  };

  const aoRolar = (e: React.WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    mudarZoom(e.deltaY < 0 ? 1 : -1);
  };

  const foto = fotos[indice];
  if (!foto) return null;

  // A ampliação gerada na importação só compensa a partir do momento em que a
  // imagem passa do tamanho nativo; no ajuste inicial o original é mais leve.
  const fonte = zoom > 1 && foto.urlHd ? foto.urlHd : foto.url;
  const nativa = dimensao ? `${dimensao.w} × ${dimensao.h} px` : null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
      onClick={onFechar}
    >
      <div
        className="w-full max-w-6xl h-full max-h-[92vh] flex flex-col bg-white dark:bg-schmersal-gray-darker rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
          <div className="min-w-0">
            <p className="text-sm font-bold text-schmersal-blue dark:text-white truncate">
              {foto.legenda ?? 'Foto'}
            </p>
            <p className="text-xs text-schmersal-gray-medium">
              {fotos.length > 1 && `${indice + 1} de ${fotos.length}`}
              {fotos.length > 1 && nativa && ' · '}
              {nativa && `foto da AR: ${nativa}`}
            </p>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => mudarZoom(-1)}
              disabled={nivelZoom === 0}
              title="Reduzir (tecla −)"
              className="p-2 rounded-lg text-schmersal-gray-dark dark:text-schmersal-gray-light hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark disabled:opacity-30"
            >
              <ZoomOut className="h-4 w-4" />
            </button>
            <button
              onClick={redefinirZoom}
              title="Voltar ao tamanho do card (tecla 0)"
              className="px-2 py-1 rounded-lg text-xs font-semibold tabular-nums text-schmersal-gray-dark dark:text-schmersal-gray-light hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark min-w-[3rem]"
            >
              {zoom}×
            </button>
            <button
              onClick={() => mudarZoom(1)}
              disabled={nivelZoom === ZOOMS.length - 1}
              title="Ampliar (tecla +)"
              className="p-2 rounded-lg text-schmersal-gray-dark dark:text-schmersal-gray-light hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark disabled:opacity-30"
            >
              <ZoomIn className="h-4 w-4" />
            </button>
            <a
              href={toAbsoluteFotoUrl(fonte)}
              target="_blank"
              rel="noreferrer"
              title="Abrir a imagem em outra aba"
              className="p-2 rounded-lg text-schmersal-gray-dark dark:text-schmersal-gray-light hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
            <button
              onClick={onFechar}
              title="Fechar (Esc)"
              className="p-2 rounded-lg text-schmersal-gray-dark dark:text-schmersal-gray-light hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="relative flex-1 min-h-0 bg-schmersal-gray-light/60 dark:bg-schmersal-gray-darkest">
          <div
            ref={areaRef}
            onWheel={aoRolar}
            onPointerDown={iniciarArraste}
            onPointerMove={moverArraste}
            onPointerUp={encerrarArraste}
            onPointerCancel={encerrarArraste}
            className={`absolute inset-0 overflow-auto flex p-4 ${
              zoom === 1 ? 'items-center justify-center' : 'items-start justify-start'
            } ${zoom === 1 ? '' : arrastando ? 'cursor-grabbing' : 'cursor-grab'}`}
            style={zoom > 1 ? { touchAction: 'none' } : undefined}
          >
            <img
              src={toAbsoluteFotoUrl(fonte)}
              alt={foto.legenda ?? ''}
              draggable={false}
              onLoad={(e) => {
                // A dimensão informada é sempre a da foto do documento, mesmo
                // quando a ampliação está em uso — é ela que diz o limite real.
                if (!foto.urlHd || fonte === foto.url) {
                  const img = e.currentTarget;
                  setDimensao({ w: img.naturalWidth, h: img.naturalHeight });
                }
              }}
              onDoubleClick={() => (nivelZoom === ZOOMS.length - 1 ? redefinirZoom() : mudarZoom(1))}
              style={
                zoom === 1
                  ? { maxWidth: '100%', maxHeight: '100%' }
                  : { width: `${zoom * 100}%`, maxWidth: 'none', flexShrink: 0 }
              }
              className="object-contain rounded-lg shadow-sm select-none"
            />
          </div>

          {fotos.length > 1 && (
            <>
              <button
                onClick={() => trocar(-1)}
                title="Anterior (←)"
                className="absolute left-3 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-white/90 dark:bg-schmersal-gray-darker/90 shadow-lg text-schmersal-blue dark:text-white hover:bg-white dark:hover:bg-schmersal-gray-darker"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => trocar(1)}
                title="Próxima (→)"
                className="absolute right-3 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-white/90 dark:bg-schmersal-gray-darker/90 shadow-lg text-schmersal-blue dark:text-white hover:bg-white dark:hover:bg-schmersal-gray-darker"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}

          {zoom > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60 text-white text-[11px] font-semibold pointer-events-none">
              <Move className="h-3 w-3" /> arraste para navegar
            </div>
          )}
        </div>

        <div className="px-4 py-2.5 border-t border-schmersal-gray-light dark:border-schmersal-gray-dark">
          <p className="text-[11px] text-schmersal-gray-medium">
            Arraste para navegar · duplo clique ou Ctrl + rolagem para ampliar · setas trocam de foto · Esc fecha
            {dimensao && dimensao.w < 700 && (
              <> · esta é a resolução que a AR guarda; para mais contexto, abra o PDF abaixo do card da parte</>
            )}
          </p>
        </div>

        {fotos.length > 1 && (
          <div className="flex gap-1.5 px-4 pb-3 overflow-x-auto">
            {fotos.map((f, i) => (
              <button
                key={`${f.url}-${i}`}
                onClick={() => { setIndice(i); redefinirZoom(); setDimensao(null); }}
                title={f.legenda ?? undefined}
                className={`flex-shrink-0 rounded overflow-hidden border-2 transition-colors ${
                  i === indice ? 'border-schmersal-turquoise' : 'border-transparent hover:border-schmersal-gray'
                }`}
              >
                <img src={toAbsoluteFotoUrl(f.url)} alt="" loading="lazy" className="h-14 w-20 object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
